import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { CompanyOnboardingService } from '../services/company-onboarding.service';
import { ToastrService } from 'ngx-toastr';
import { AuthServiceService } from '@edtech-ui/core/services/authService/auth-service.service';
import { LoginPayload } from '@edtech-ui/core/models/interfaces/login';
import { finalize } from 'rxjs';
import { passwordStrengthValidator, passwordCrossFieldValidator } from '@edtech-ui/core/utils/password-validators';
import { emailOrPhoneValidator } from '@edtech-ui/core/utils/identifier-validator';
import { jwtDecode } from 'jwt-decode';
import { RouterModule } from '@angular/router';

export function phoneValidator(): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		const value = control.value;
		if (value === null || value === undefined || value === '') {
			return null;
		}

		const stringValue = typeof value === 'string' ? value : String(value);
		const trimmedValue = stringValue.trim();

		// Check if it contains only digits and optionally a leading '+'
		const onlyDigitsAndPlus = /^(\+)?\d+$/.test(trimmedValue);
		if (!onlyDigitsAndPlus) {
			return { onlyDigits: true };
		}

		// Check format: optional country code (with +) followed by a 10-digit number starting with 6,7,8,9
		const validFormat = /^(\+\d+)?[6-9]\d{9}$/.test(trimmedValue);
		if (!validFormat) {
			return { invalidPhone: true };
		}

		return null;
	};
}
@Component({
	selector: 'app-recruiter-auth',
	standalone: true,
	imports: [CommonModule, ReactiveFormsModule, RouterModule],
	templateUrl: './recruiter-auth.component.html',
	styleUrls: ['./recruiter-auth.component.scss']
})
export class RecruiterAuthComponent implements OnInit {
	isLogin: boolean = true;
	loginForm!: FormGroup;
	signupForm!: FormGroup;
	showPassword: boolean = false;
	showConfirmPassword: boolean = false;
	isLoading: boolean = false;
	loginMethod: 'password' | 'otp' = 'password';
	isVerifyingOtp: boolean = false;
	otpTarget: string = '';
	otpContext: 'login' | 'register' = 'login';
	onboardingCompanyId: string = '';

	// Forgot Password Flow
	forgotPasswordStep: number = 0; // 0 = not in flow, 1 = Account, 2 = Verify, 3 = Reset, 4 = Success
	forgotPasswordAccountForm!: FormGroup;
	forgotPasswordResetForm!: FormGroup;
	forgotPasswordEmailOrPhone: string = '';
	forgotPasswordOtpValues: string[] = new Array(6).fill('');
	forgotPasswordResetToken: string = '';
	showFpPassword = false;
	showFpConfirmPassword = false;
	isResendingOtp: boolean = false;
	resendCountdown: number = 0;
	resendInterval: any;

	constructor(
		private fb: FormBuilder,
		private router: Router,
		private onboardingService: CompanyOnboardingService,
		private toastr: ToastrService,
		private _authService: AuthServiceService
	) { }

	ngOnInit(): void {
		this.initForms();
	}

	togglePasswordVisibility(): void {
		this.showPassword = !this.showPassword;
	}

	toggleConfirmPasswordVisibility(): void {
		this.showConfirmPassword = !this.showConfirmPassword;
	}

	trimPhoneValue(controlName: string, form: FormGroup): void {
		const control = form.get(controlName);
		if (control && typeof control.value === 'string') {
			control.setValue(control.value.trim(), { emitEvent: false });
		}
	}

	stripSpaces(event: Event, controlName: string, form: FormGroup): void {
		const input = event.target as HTMLInputElement;
		const stripped = input.value.replace(/\s/g, '');
		if (input.value !== stripped) {
			input.value = stripped;
		}
		const control = form.get(controlName);
		if (control) {
			control.setValue(stripped, { emitEvent: true });
		}
	}

	onPhoneInput(event: Event, controlName: string, form: FormGroup): void {
		const input = event.target as HTMLInputElement;
		let value = input.value.replace(/[^0-9+]/g, '');

		const hasPlus = value.startsWith('+');
		value = value.replace(/\+/g, '');
		if (hasPlus) {
			value = '+' + value;
		}

		const maxLength = hasPlus ? 14 : 10;
		if (value.length > maxLength) {
			value = value.slice(0, maxLength);
		}

		const control = form.get(controlName);
		if (control) {
			control.setValue(value, { emitEvent: false });
		}
	}

	onIdentifierInput(event: Event, controlName: string, form: FormGroup): void {
		const input = event.target as HTMLInputElement;
		let rawValue = input.value;

		const control = form.get(controlName);
		if (!control) return;
		control.markAsTouched();

		if (/^\d+$/.test(rawValue) && rawValue.length > 10) {
			rawValue = rawValue.slice(0, 10);
			input.value = rawValue;
		}

		control.setValue(rawValue, { emitEvent: false });
	}

	initForms(): void {
		this.loginForm = this.fb.group({
			identifier: ['', [Validators.required, emailOrPhoneValidator]],
			password: [''],
			otp: ['']
		});

		this.signupForm = this.fb.group({
			company_name: ['', [
				Validators.required,
				Validators.minLength(2),
				Validators.maxLength(100),
				Validators.pattern('^(?!^[&.\\-\\s]+$)[a-zA-Z0-9\\s&.\\-]+$')
			]],
			company_url: ['', [
				Validators.required,
				Validators.pattern('^(https?:\\/\\/)?(www\\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\\.[a-zA-Z0-9()]{1,6}\\b([-a-zA-Z0-9()@:%_\\+.~#?&//=]*)$')
			]],
			industry: ['', Validators.required],
			custom_industry: [''],
			poc_name: ['', [
				Validators.required,
				Validators.minLength(2),
				Validators.maxLength(50),
				Validators.pattern('^[a-zA-Z\\s.]+$')
			]],
			poc_email: ['', [Validators.required, Validators.email]],
			official_email: ['', [Validators.required, Validators.email]],
			poc_phone: ['', [
				Validators.required,
				phoneValidator()
			]],
			password: ['', [
				Validators.required,
				Validators.minLength(8),
				Validators.maxLength(32),
				passwordStrengthValidator
			]],
			confirm_password: ['', [Validators.required]]
		}, { validators: passwordCrossFieldValidator });

		this.forgotPasswordAccountForm = this.fb.group({
			identifier: ['', [Validators.required, Validators.email]]
		});

		this.forgotPasswordResetForm = this.fb.group({
			password: ['', [
				Validators.required,
				Validators.minLength(8),
				passwordStrengthValidator
			]],
			confirm_password: ['', Validators.required]
		}, { validators: passwordCrossFieldValidator });

		this.signupForm.get('industry')?.valueChanges.subscribe(val => {
			const customIndustryControl = this.signupForm.get('custom_industry');
			if (val === 'Other') {
				customIndustryControl?.setValidators([
					Validators.required,
					Validators.minLength(2),
					Validators.maxLength(30),
					Validators.pattern('^[a-zA-Z\\s]+$')
				]);
			} else {
				customIndustryControl?.clearValidators();
				customIndustryControl?.setValue('');
			}
			customIndustryControl?.updateValueAndValidity();
		});

		this.setLoginValidators();
	}


	setLoginValidators(): void {
		const identifierControl = this.loginForm.get('identifier');
		const passwordControl = this.loginForm.get('password');
		const otpControl = this.loginForm.get('otp');

		identifierControl?.clearValidators();
		passwordControl?.clearValidators();
		otpControl?.clearValidators();

		if (this.isVerifyingOtp) {
			otpControl?.setValidators([Validators.required, Validators.pattern('^[0-9]{6}$')]);
		} else {
			identifierControl?.setValidators([Validators.required, emailOrPhoneValidator]);
			if (this.loginMethod === 'password') {
				passwordControl?.setValidators([Validators.required]);
			}
		}

		identifierControl?.updateValueAndValidity();
		passwordControl?.updateValueAndValidity();
		otpControl?.updateValueAndValidity();
	}


	toggleAuth(type: 'login' | 'signup'): void {
		this.isLogin = type === 'login';
		if (this.isLogin) {
			this.otpContext = 'login';
			this.isVerifyingOtp = false;
			this.setLoginValidators();
		} else {
			this.otpContext = 'register';
		}
	}


	switchLoginMethod(method: 'password' | 'otp'): void {
		this.loginMethod = method;
		this.isVerifyingOtp = false;
		this.setLoginValidators();
	}




	sendOtp(isResend: boolean = false): void {

		if (!isResend && this.loginForm.invalid) {
			this.loginForm.markAllAsTouched();
			return;
		}

		const payload: any = {
			is_default: false,
			login_source: 'web',
			fcm_token: ''
		};

		const identifier = this.loginForm.value.identifier;
		const isPhone = /^[6-9]\d{9}$/.test(identifier) || /^\d+$/.test(identifier);

		if (isPhone) {
			payload.phone = identifier;
		} else {
			payload.email = identifier;
		}
		this.otpTarget = identifier;

		this.isLoading = true;
		this._authService.postLogin(payload).pipe(
			finalize(() => {
				this.isLoading = false;
			})
		).subscribe({
			next: (response) => {
				if (response.Success) {
					this.isVerifyingOtp = true;
					this.setLoginValidators();
					this.toastr.success(isResend ? 'OTP resent successfully' : 'OTP sent successfully');
					if (response.Data?.company_id) {
						localStorage.setItem('company_id', response.Data.company_id);
					}
				} else {
					this.toastr.error(response.Message);
				}
			},
			error: () => {
				this.toastr.error(isResend ? 'Failed to resend OTP' : 'Failed to send OTP');
			}
		});
	}


	changeOtpTarget(): void {
		this.isVerifyingOtp = false;
		const otpControl = this.loginForm.get('otp');
		if (otpControl) {
			otpControl.setValue('');
			otpControl.markAsUntouched();
			otpControl.markAsPristine();
		}
		this.setLoginValidators();
	}

	// ============================================
	// FORGOT PASSWORD FLOW
	// ============================================

	goToForgotPassword() {
		this.forgotPasswordStep = 1;
		this.forgotPasswordAccountForm.reset();
		this.forgotPasswordEmailOrPhone = '';
	}

	forgotPasswordBack() {
		if (this.forgotPasswordStep > 1 && this.forgotPasswordStep < 4) {
			this.forgotPasswordStep--;
		} else {
			this.forgotPasswordStep = 0;
		}
	}

	startResendTimer() {
		this.resendCountdown = 30;
		if (this.resendInterval) clearInterval(this.resendInterval);
		this.resendInterval = setInterval(() => {
			this.resendCountdown--;
			if (this.resendCountdown <= 0) {
				clearInterval(this.resendInterval);
			}
		}, 1000);
	}

	async submitForgotPasswordAccount() {
		if (this.forgotPasswordAccountForm.invalid) {
			this.forgotPasswordAccountForm.markAllAsTouched();
			return;
		}
		this.isLoading = true;
		const identifier = this.forgotPasswordAccountForm.value.identifier;
		const payload = {
			phone_email: identifier,
			fcm_token: ''
		};

		this._authService.postForgotPassword(payload).subscribe({
			next: (response: any) => {
				this.isLoading = false;
				if (response.Success) {
					this.toastr.success(response.Message);
					this.forgotPasswordEmailOrPhone = response.Data?.Email || response.Data?.phone_email || identifier;
					this.forgotPasswordOtpValues.fill('');
					this.forgotPasswordStep = 2;
					this.startResendTimer();
				} else {
					this.toastr.error(response.Message);
				}
			},
			error: () => {
				this.isLoading = false;
				this.toastr.error('Something went wrong while sending OTP.');
			}
		});
	}

	trackByIndex(index: number, obj: any): any {
		return index;
	}

	handleFpOtpInput(event: any, index: number) {
		const input = event.target as HTMLInputElement;
		const value = input.value;

		if (!/^\d$/.test(value)) {
			input.value = '';
			this.forgotPasswordOtpValues[index] = '';
			return;
		}

		this.forgotPasswordOtpValues[index] = value;

		if (value && index < 5) {
			const nextInput = input.nextElementSibling as HTMLInputElement;
			nextInput?.focus();
		}
	}

	handleFpKeyDown(event: KeyboardEvent, index: number) {
		const target = event.target as HTMLInputElement;
		if (event.key === 'Backspace' && !this.forgotPasswordOtpValues[index] && index > 0) {
			const prevInput = target.previousElementSibling as HTMLInputElement;
			prevInput?.focus();
		}
	}

	handleFpOtpPaste(event: ClipboardEvent) {
		event.preventDefault();
		const clipboardData = event.clipboardData || (window as any).clipboardData;
		const pastedText = clipboardData?.getData('text');
		if (!pastedText) return;

		const numbers = pastedText.replace(/\D/g, '').substring(0, 6);
		for (let i = 0; i < numbers.length; i++) {
			this.forgotPasswordOtpValues[i] = numbers[i];
		}

		setTimeout(() => {
			const inputs = document.querySelectorAll('.fp-otp-inputs input') as NodeListOf<HTMLInputElement>;
			if (inputs && inputs.length > 0) {
				const focusIndex = Math.min(numbers.length, 5);
				inputs[focusIndex].focus();
			}
		}, 10);
	}

	isFpOtpFilled(): boolean {
		return this.forgotPasswordOtpValues.join('').length === 6;
	}

	getFpOtpCode(): string {
		return this.forgotPasswordOtpValues.join('');
	}

	resendFpOtp() {
		if (this.resendCountdown > 0) return;
		this.isResendingOtp = true;
		this._authService.postResendOtp(this.forgotPasswordEmailOrPhone).subscribe({
			next: (response: any) => {
				this.isResendingOtp = false;
				if (response?.Success) {
					this.toastr.success(response.Message || 'OTP sent successfully');
					this.startResendTimer();
				} else {
					this.toastr.error(response.Message || 'Failed to resend OTP');
				}
			},
			error: () => {
				this.isResendingOtp = false;
				this.toastr.error('Something went wrong while resending OTP');
			}
		});
	}

	submitForgotPasswordVerify() {
		const otp = this.getFpOtpCode();
		if (otp.length === 6 && /^\d{6}$/.test(otp)) {
			this.isLoading = true;
			const data = {
				email: this.forgotPasswordEmailOrPhone,
				otp: Number(otp),
			};
			this._authService.postVerifyResetOtp(data).subscribe({
				next: (response: any) => {
					this.isLoading = false;
					if (response?.Success) {
						this.toastr.success('OTP Verified');
						this.forgotPasswordResetToken = response.Data.reset_token;
						this.forgotPasswordResetForm.reset();
						this.forgotPasswordStep = 3;
					} else {
						this.toastr.error(response.Message || 'Invalid OTP');
						this.forgotPasswordOtpValues.fill('');
						setTimeout(() => {
							const firstInput = document.getElementById('fp-otp-input-0') as HTMLInputElement;
							if (firstInput) firstInput.focus();
						}, 0);
					}
				},
				error: () => {
					this.isLoading = false;
					this.toastr.error('Verification failed. Please try again.');
					this.forgotPasswordOtpValues.fill('');
					setTimeout(() => {
						const firstInput = document.getElementById('fp-otp-input-0') as HTMLInputElement;
						if (firstInput) firstInput.focus();
					}, 0);
				}
			});
		} else {
			this.toastr.error('Invalid OTP format');
		}
	}

	submitForgotPasswordReset() {
		if (this.forgotPasswordResetForm.invalid) {
			this.forgotPasswordResetForm.markAllAsTouched();
			return;
		}
		this.isLoading = true;
		const payload = {
			new_password: this.forgotPasswordResetForm.value.password,
			email: this.forgotPasswordEmailOrPhone,
			reset_token: this.forgotPasswordResetToken,
		};

		this._authService.postSetNewPassword(payload).subscribe({
			next: (response: any) => {
				this.isLoading = false;
				if (response.Success) {
					this.toastr.success(response.Message);
					this.forgotPasswordStep = 4;
				} else {
					this.toastr.error(response.Message);
				}
			},
			error: () => {
				this.isLoading = false;
				this.toastr.error('Something went wrong while resetting password.');
			}
		});
	}

	verifyRegistrationOtp(otp: string): void {
		const requestData = {
			company_id: this.onboardingCompanyId,
			poc_email: this.otpTarget,
			otp: parseInt(otp, 10)
		};

		this.isLoading = true;
		this.onboardingService.verifyOtp(requestData).subscribe({
			next: (res) => {
				this.isLoading = false;
				if (res.Success) {
					this.toastr.success(res.Message || 'OTP verified successfully.');
					// Do not overwrite company registration data here; it was saved during onSignup.
					if (res.Data?.session_token) {
						localStorage.setItem('session_token', res.Data.session_token);
					}
					if (res.Data?.company_id) {
						localStorage.setItem('company_id', res.Data.company_id);
					}
					this.router.navigate(['recruiter-dashboard/company-onBoard']);
				} else {
					this.toastr.error(res.Message || 'Invalid OTP.');
				}
			},
			error: (err) => {
				this.isLoading = false;
				console.error('OTP Verification failed', err);
				const errMsg = err.error?.Message || 'Verification failed. Please try again.';
				const status = err.status;

				if (status === 404) {
					this.toastr.error('Registration session not found. Starting over.');
					this.toggleAuth('signup');
				} else if (status === 400) {
					this.toastr.error(errMsg);
					if (errMsg.toLowerCase().includes('expired')) {
						this.loginForm.get('otp')?.setValue('');
					} else {
						this.loginForm.get('otp')?.setErrors({ 'incorrect': true });
					}
				} else {
					this.toastr.error(errMsg);
				}
			}
		});
	}


	resendOtp(): void {
		const otpControl = this.loginForm.get('otp');
		if (otpControl) {
			otpControl.setValue('');
			otpControl.markAsUntouched();
			otpControl.markAsPristine();
		}
		if (this.otpContext === 'register') {
			this.onSignup();
		} else {
			this.sendOtp(true);
		}
	}


	onSignup(): void {
		if (this.signupForm.valid) {
			const formData = this.signupForm.value;
			let finalIndustry = formData.industry;
			if (finalIndustry === 'Other') {
				finalIndustry = formData.custom_industry?.trim();
			}

			const requestData = {
				company_name: formData.company_name?.trim(),
				company_url: formData.company_url?.trim(),
				industry: finalIndustry,
				poc_name: formData.poc_name?.trim(),
				poc_email: formData.poc_email?.trim(),
				poc_phone: formData.poc_phone?.trim(),
				official_email: formData.official_email?.trim(),
				password: formData.password
			};

			this.isLoading = true;
			this.onboardingService.onboardCompany(requestData).subscribe({
				next: (res) => {
					this.isLoading = false;
					if (res.Success && res.Data?.company_id) {
						this.onboardingCompanyId = res.Data.company_id;
						if (res.Data?.company_id) {
							localStorage.setItem('company_id', res.Data.company_id);
						}
						this.toastr.success(res.Message || 'OTP sent to your email. Please verify to continue.');

						// Store the form data in the service so it can be pre-filled on the next screen
						this.onboardingService.setCompanyRegistrationData(requestData);

						this.otpContext = 'register';
						this.isLogin = true;
						this.loginMethod = 'otp';
						this.isVerifyingOtp = true;
						this.otpTarget = requestData.poc_email;

						this.loginForm.get('otp')?.setValue('');
						this.setLoginValidators();
					} else {
						this.toastr.error(res.Message || 'Registration failed. Please try again.');
					}
				},
				error: (err) => {
					this.isLoading = false;
					console.error('Onboarding failed', err);
					this.toastr.error(err.error?.Message || 'Registration failed. Please try again.');
				}
			});
		} else {
			this.signupForm.markAllAsTouched();
		}
	}


	onLogin(): void {
		if (this.otpContext === 'register') {
			const otpValue = this.loginForm.get('otp')?.value;
			if (!otpValue) {
				return;
			}
			this.verifyRegistrationOtp(otpValue);
			return;
		}

		if (!this.isVerifyingOtp && this.loginMethod === 'otp') {
			this.sendOtp();
			return;
		}

		const verifyPayload: any = {
			is_default: false,
			login_source: 'web',
			fcm_token: '',
			country: 'US',
			user_timezone: 'Asia/Kolkata',
			onesignal_player_id: ''
		};

		const identifier = this.loginForm.value.identifier;
		const isPhone = /^[6-9]\d{9}$/.test(identifier) || /^\d+$/.test(identifier);

		if (isPhone) {
			verifyPayload.phone = identifier;
		} else {
			verifyPayload.email = identifier;
		}

		if (this.isVerifyingOtp) {
			const otpValue = this.loginForm.get('otp')?.value;
			if (!otpValue) {
				return;
			}
			verifyPayload.otp = Number(otpValue);
		} else if (this.loginMethod === 'otp') {
			const otpValue = this.loginForm.get('otp')?.value;
			if (!otpValue) {
				return;
			}
			verifyPayload.otp = otpValue;
		} else {
			verifyPayload.password = this.loginForm.value.password;
		}

		this.isLoading = true;

		this._authService.postLogin(verifyPayload).pipe(
			finalize(() => {
				this.isLoading = false;
			})
		).subscribe({
			next: (response) => {

				if (response.Success) {
					const tokenStr = response.Data?.token || '';
					let isCompanyAdmin = false;
					if (tokenStr) {
						const decoded: any = jwtDecode(tokenStr);
						const permissions: string[] = decoded.permissions || [];
						isCompanyAdmin = permissions.includes('company:manage');
						const hasProctorAccess = permissions.some((p: string) => p.startsWith('proctoring:'));

						if (!isCompanyAdmin && !hasProctorAccess) {
							this.toastr.error('You do not have access to this platform.', 'Access Denied');
							return;
						}
					}

					localStorage.setItem('token', tokenStr);
					this.toastr.success('Login successful');

					const newUserId = response.Data.user_id.toString();
					localStorage.setItem('user_id', response.Data.user_id.toString());
					this._authService.currentUserId.next(newUserId);


					if (response.Data?.phone_email) {
						this._authService.currentUserEmail.next(response.Data.phone_email);
						localStorage.setItem('phone_email', response.Data.phone_email);
					}

					if (response.Data?.company_id) {
						localStorage.setItem('company_id', response.Data.company_id);
					}

					if (response.Data?.token) {
						this._authService.setToken(response.Data.token);
					}

					if (response.Data.refresh_token) {
						localStorage.setItem('refresh_token', response.Data.refresh_token);
					}

					if (response.Data?.modules) {
						this._authService.setModules(response.Data.modules);
					}

					const landingRoute = this._authService.getFirstAllowedRoute();

					if (isCompanyAdmin) {
						this.onboardingService.getCompanyProfile().subscribe({
							next: (profileRes) => {
								this.toastr.success('Login successful');
								if (profileRes && profileRes.Success && profileRes.Data) {
									const status = profileRes.Data.status;
									if (status === 'pending' || status === 'rejected' || status === 'need_clarification' || status === 'clarification') {
										this.router.navigate(['/company-status']);
									} else {
										this.router.navigate([landingRoute]);
									}
								} else {
									this.router.navigate([landingRoute]);
								}
							},
							error: (err) => {
								this.toastr.success('Login successful');
								console.error('Error fetching company profile on login', err);
								if (err?.status === 403) {
									this.router.navigate(['/company-status']);
								} else {
									this.router.navigate([landingRoute]);
								}
							}
						});
					} else {
						this.toastr.success('Login successful');
						this.router.navigate([landingRoute]);
					}

				} else {
					this.toastr.error(response.Message);
				}
			},
			error: () => {

				this.toastr.error('OTP verification failed');
			}
		});
	}

	toTitleCase(str: string): string {
		if (!str) return '';
		return str.toLowerCase().split(' ').map(word => {
			return word.charAt(0).toUpperCase() + word.slice(1);
		}).join(' ');
	}

	resetIndustry(): void {
		this.signupForm.get('industry')?.setValue('');
		this.signupForm.get('custom_industry')?.setValue('');
	}

	preventNumbers(event: Event): void {
		const input = event.target as HTMLInputElement;
		const sanitizedValue = input.value.replace(/[0-9]/g, '');
		if (input.value !== sanitizedValue) {
			input.value = sanitizedValue;
			this.signupForm.get('custom_industry')?.setValue(sanitizedValue, { emitEvent: false });
		}
	}

	ngOnDestroy(): void {
		if (this.resendInterval) clearInterval(this.resendInterval);
	}
}


